import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/react';
import { WebChatBannerSettings, WebChatColorProps, WebChatThemeProps } from "@42maru/webchat";
import { isAxiosError } from '@/core/error';
import { WEBCHAT_HOST } from '@/core/variables';
import { ScenarioProperties } from '@/model';
import { refineSameNameFiles, replaceBlobUrlToFilenameAndGetNamedFiles } from '@/util/fileUtil';
import {
    isValidTitle, isValidAlias, isValidBottomMenu, isValidEventCards, isValidIntroSubtitleText,
    isValidIntroTitleText, isValidBannerSettings, mergeThemeWithDefault, isValidAutoCompleteLimit
} from '@/util/helper/webchatThemeHelper';
import { useIntegration, useUpdateWebchatTheme } from '@/query/integration';
import { useScenario, useUpdateScenario } from '@/query/scenario';
import { useScenarioId, useUploadedFiles, useWebchatPreviewMode, useSyncForm } from '@/hook';
import Button from '@/component/Button';
import { SwitchInput } from '@/component/input';
import { errorToast, successToast } from '@/component/Toast';
import WebchatDisclosure from './component/WebchatDisclosure';
import WebchatSettingPreview from './preview/WebchatSettingPreview';
import {
    AvatarInputGroup, ChatWindowInputGroup, MessageBoxInputGroup, EtcInputGroup, ChatHomeInputGroup,
    HomeColorInputGroup, BannerButtonInputGroup, ChatWindowColorInputGroup, InstallationInputGroup,
    MessageBoxColorInputGroup, WebchatBaseInputGroup
 } from './inputGroup';


type SettingTabType = "webchat" | "layout" | "style" | "banner";

const WEBCHAT_SETTING_TABS: { key: SettingTabType, labelIntl: string }[] = [
    { key: 'webchat', labelIntl: "b0024" },
    { key: 'layout', labelIntl: 'i09001' },
    { key: 'style', labelIntl: 'i09002' },
    { key: 'banner', labelIntl: 'i09003' }
];

const WebchatIntegrationView: React.FC = () => {
    const scenarioId = useScenarioId();
    const intl = useIntl();
    const { files, clear: clearFiles } = useUploadedFiles();
    const { onChangeActiveTab } = useWebchatPreviewMode();
    const { syncForm } = useSyncForm();

    const [webchatTheme, setWebchatTheme] = useState<WebChatThemeProps>();
    const [webchatProperties, setWebchatProperties] = useState<ScenarioProperties>();
    const [activeTab, setActiveTab] = useState<SettingTabType>('webchat');
    const [accordionKey, setAccordionKey] = useState<number | undefined>(undefined);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [isValidUpdateForm, setIsValidUpdateForm] = useState<boolean>(false);

    const { data: scenarioData } = useScenario(scenarioId);
    const { data: integrationData } = useIntegration(scenarioId);
    const { mutateAsync: updateWebchatTheme } = useUpdateWebchatTheme(scenarioId);
    const { mutateAsync: updateScenarioProperties } = useUpdateScenario(scenarioId);

    const onSubmit = async () => {
        setIsUpdating(true);

        if (!webchatTheme || !isValidUpdateForm) {
            errorToast(intl.formatMessage({ id: "i09004" }));
            setIsUpdating(false);
            return;
        }

        const refinedFiles = await refineSameNameFiles(files);
        const {
            data: theme,
            files: filesToSubmit
        } = replaceBlobUrlToFilenameAndGetNamedFiles(structuredClone(webchatTheme), refinedFiles);

        theme.embeddedSettings!.webchatHost = `${WEBCHAT_HOST}`.replace("https://", "").replace("http://", "");
        if (theme.alias) {
            theme.alias = theme.alias.trim();
        }

        // TODO webchat에서 대화 메뉴 분리 전까지 한시적으로 적용
        if (!theme.introCardOrder?.find((item: any) => item.key === "previous_conversations")) {
            theme.introCardOrder?.push({ key: "previous_conversations", disable: true });
        }

        const formData = new FormData();
        Array.from(filesToSubmit).forEach(file => {
            formData.append("files", file);
        });
        formData.append("publisher_property", JSON.stringify({
            ...integrationData,
            webchat_theme: theme,
        }));

        try {
            await updateWebchatTheme(formData);
            clearFiles();
            await updateScenarioProperties({ properties: webchatProperties });
            successToast(intl.formatMessage({ id: "t003" }));
            setIsValidUpdateForm(false);
        } catch (e) {
            if (isAxiosError(e) && e.response?.status === 422) {
                errorToast(intl.formatMessage({ id: "i09005" }));
            } else {
                errorToast(intl.formatMessage({ id: "t004" }));
            }
        }
        setIsUpdating(false);
    };

    const onClickTab = (tab: SettingTabType) => {
        if (activeTab === tab) {
            return;
        }
        setActiveTab(tab);
        onChangeActiveTab(tab);
        setWebchatTheme(mergeThemeWithDefault(integrationData?.webchat_theme));
        setIsValidUpdateForm(false);
        clearFiles();
        setAccordionKey(["layout", "style"].includes(tab) ? 1 : 0);
    };

    const onClickAccordion = (key: number) => {
        setAccordionKey(prev => prev === key ? undefined : key);
    };

    const defaultWebchatTheme = useMemo(() => {
        return mergeThemeWithDefault(integrationData?.webchat_theme);
    }, [integrationData?.webchat_theme]);

    const resetData = useCallback((theme: WebChatThemeProps, properties: ScenarioProperties) => {
        setWebchatTheme(theme);
        setWebchatProperties(properties);
        setTimeout(() => {
            syncForm();
        }, 100);
    }, [syncForm]);

    useEffect(() => {
        resetData(defaultWebchatTheme, structuredClone(scenarioData?.properties ?? {}));
    }, [defaultWebchatTheme, scenarioData?.properties, resetData]);

    useEffect(() => {
        onChangeActiveTab(activeTab);
    }, [activeTab, onChangeActiveTab]);

    if (!webchatTheme) {
        return null;
    }
    return (
        <TabGroup as="main"
                  className="scenario-content-layout pt-0 flex flex-col gap-4"
                  selectedIndex={WEBCHAT_SETTING_TABS.findIndex(t => t.key === activeTab)}
                  onChange={index => onClickTab(WEBCHAT_SETTING_TABS[index].key)}
        >
            <TabList aria-label="웹챗 설정"
                     className="w-full flex items-center gap-2"
            >
                {WEBCHAT_SETTING_TABS.map(tab => (
                    <Tab key={tab.key}
                         className="tab-btn text-black-500 font-[400] border-transparent aria-selected:text-chat42 aria-selected:font-[600] aria-selected:border-chat42"
                    >
                        {intl.formatMessage({ id: tab.labelIntl })}
                    </Tab>
                ))}
            </TabList>
            <div className="h-full w-fit min-h-0 flex gap-2">
                <div className="min-w-fit flex flex-col gap-2 overflow-y-hidden">
                    <TabPanels as="div"
                               className="scroll-gap-container overflow-y-scroll"
                    >
                            <TabPanel className="w-sm-page flex flex-col gap-2">
                                <div className="webchat-setting-card webchat-setting-card-body">
                                    <WebchatBaseInputGroup theme={webchatTheme}
                                                           legend={intl.formatMessage({ id: "b0024" })}
                                                           onChange={theme => {
                                                               setWebchatTheme(theme);
                                                               setIsValidUpdateForm(
                                                                    isValidTitle(theme.title ?? "").isValid
                                                                        && isValidAlias(theme.alias).isValid
                                                                );
                                                           }}
                                    />
                                </div>
                            </TabPanel>
                            <TabPanel className="w-sm-page flex flex-col gap-2">
                                <LayoutSettingAccordion theme={webchatTheme}
                                                        accordionKey={accordionKey}
                                                        onToggle={onClickAccordion}
                                                        onChange={(theme, isValid) => {
                                                            setWebchatTheme(theme);
                                                            setIsValidUpdateForm(isValid);
                                                        }}
                                />
                            </TabPanel>
                            <TabPanel className="w-sm-page flex flex-col gap-2">
                                <StyleSettingAccordion colors={webchatTheme.colors!}
                                                       accordionKey={accordionKey}
                                                       onToggle={onClickAccordion}
                                                       onChange={(colors, isValid) => {
                                                            setWebchatTheme({ ...webchatTheme, colors });
                                                            setIsValidUpdateForm(isValid);
                                                       }}
                                />
                            </TabPanel>
                            <TabPanel className="w-sm-page flex flex-col gap-2">
                                <BannerSettingAccordion embeddedSettings={webchatTheme.embeddedSettings!}
                                                        accordionKey={accordionKey}
                                                        onToggle={onClickAccordion}
                                                        onChange={(embeddedSettings, isValid) => {
                                                            setWebchatTheme({ ...webchatTheme, embeddedSettings });
                                                            setIsValidUpdateForm(isValid);
                                                        }}
                                />
                            </TabPanel>
                    </TabPanels>
                    <div className="scroll-gap-container-align self-end">
                        <Button size="sm"
                                variant="primary"
                                disabled={isUpdating || !isValidUpdateForm}
                                onClick={onSubmit}
                        >
                            {intl.formatMessage({ id: 'b0007' })}
                        </Button>
                    </div>
                </div>
                <WebchatSettingPreview theme={webchatTheme}
                                       properties={webchatProperties}
                                       scenarioId={scenarioId}
                />
            </div>
        </TabGroup>
    );
};

interface LayoutSettingAccordionProps {
    accordionKey?: number;
    theme: WebChatThemeProps;
    onChange: (theme: WebChatThemeProps, isValid: boolean) => void;
    onToggle: (key: number) => void;
}

const LayoutSettingAccordion: React.FC<LayoutSettingAccordionProps> = (props) => {
    const { accordionKey, onToggle, theme, onChange } = props;

    const intl = useIntl();

    const { setHomeMode, setChatMode } = useWebchatPreviewMode();

    const onChangeInput = (changes: WebChatThemeProps) => {
        const validIntroTitle = isValidIntroTitleText(changes.introTitleText ?? "").isValid;
        const validIntroSubtitle = isValidIntroSubtitleText(changes.introSubtitleText).isValid;
        const validEventCards = isValidEventCards(changes.introEventCards).isValid;
        const validBottomMenu = isValidBottomMenu(changes.bottomMenu).isValid;
        const validAutoComplete = isValidAutoCompleteLimit(changes.autoCompleteLimit).isValid;

        const isValid = (
            validIntroTitle &&
            validIntroSubtitle &&
            validEventCards &&
            validBottomMenu &&
            validAutoComplete
        );
        onChange({ ...theme, ...changes }, isValid);
    };

    useEffect(() => {
        switch (accordionKey) {
            case 0:
                setHomeMode();
                break;
            case 1:
            case 2:
            case 3:
                setChatMode();
                break;
            case 4:
                setChatMode("bottom-menu");
                break;
        }
        return () => {
            setChatMode();
        };
    }, [accordionKey, setHomeMode, setChatMode]);

    return (
        <div className="webchat-setting-container">
            <WebchatDisclosure isOpen={accordionKey === 0}
                               onToggle={() => onToggle(0)}
                               title={intl.formatMessage({ id: "b0060" })}
                               headItem={
                                   <SwitchInput aria-label={intl.formatMessage({ id: "b0060" })}
                                                defaultValue={theme.useIntro ?? false}
                                                onSwitch={v => onChangeInput({ ...theme, useIntro: v })}
                                   />
                               }
            >
                <ChatHomeInputGroup theme={theme}
                                    legend={intl.formatMessage({ id: "b0060" })}
                                    onChange={onChangeInput}
                                    disabled={!theme.useIntro}
                />
            </WebchatDisclosure>

            <WebchatDisclosure isOpen={accordionKey === 1}
                               onToggle={() => onToggle(1)}
                               title={intl.formatMessage({ id: "i09016" })}
            >
                <AvatarInputGroup theme={theme}
                                  legend={intl.formatMessage({ id: "i09016" })}
                                  onChange={onChangeInput}
                />
            </WebchatDisclosure>

            <WebchatDisclosure isOpen={accordionKey === 2}
                               onToggle={() => onToggle(2)}
                               title={intl.formatMessage({ id: "i09017" })}
            >
                <ChatWindowInputGroup theme={theme}
                                      legend={intl.formatMessage({ id: "i09017" })}
                                      onChange={onChangeInput}
                />
            </WebchatDisclosure>

            <WebchatDisclosure isOpen={accordionKey === 3}
                               onToggle={() => onToggle(3)}
                               title={intl.formatMessage({ id: "i09019" })}
            >
                <MessageBoxInputGroup theme={theme}
                                      legend={intl.formatMessage({ id: "i09019" })}
                                      onChange={onChangeInput}
                />
            </WebchatDisclosure>

            <WebchatDisclosure isOpen={accordionKey === 4}
                               onToggle={() => onToggle(4)}
                               title={intl.formatMessage({ id: "b0031" })}
            >
                <EtcInputGroup theme={theme}
                               legend={intl.formatMessage({ id: "b0031" })}
                               onChange={onChangeInput}
                />
            </WebchatDisclosure>
        </div>
    );
};


interface StyleSettingAccordionProps {
    accordionKey?: number;
    colors: WebChatColorProps;
    onChange: (colors: WebChatColorProps, isValid: boolean) => void;
    onToggle: (key: number) => void;
}

const StyleSettingAccordion: React.FC<StyleSettingAccordionProps> = (props) => {
    const { accordionKey, colors, onChange, onToggle } = props;

    const intl = useIntl();

    const { setHomeMode, setChatMode } = useWebchatPreviewMode();

    const onChangeInput = (changes: WebChatColorProps) => {
        onChange({ ...colors, ...changes }, true);
    };

    useEffect(() => {
        if (accordionKey === 0) {
            setHomeMode();
        } else {
            setChatMode();
        }
    }, [accordionKey, setHomeMode, setChatMode]);

    return (
        <div className="webchat-setting-container">
            <WebchatDisclosure isOpen={accordionKey === 0}
                               onToggle={() => onToggle(0)}
                               title={intl.formatMessage({ id: "b0060" })}
            >
                <HomeColorInputGroup colors={colors}
                                     legend={intl.formatMessage({ id: "b0060" })}
                                     onChange={onChangeInput}
                />
            </WebchatDisclosure>

            <WebchatDisclosure isOpen={accordionKey === 1}
                               onToggle={() => onToggle(1)}
                               title={intl.formatMessage({ id: "i09017" })}
            >
                <ChatWindowColorInputGroup colors={colors}
                                           legend={intl.formatMessage({ id: "i09017" })}
                                           onChange={onChangeInput}
                />
            </WebchatDisclosure>

            <WebchatDisclosure isOpen={accordionKey === 2}
                               onToggle={() => onToggle(2)}
                               title={intl.formatMessage({ id: "i09019" })}
            >
                <MessageBoxColorInputGroup colors={colors}
                                           legend={intl.formatMessage({ id: "i09019" })}
                                           onChange={onChangeInput}
                />
            </WebchatDisclosure>
        </div>
    );
};


interface BannerSettingAccordionProps {
    accordionKey?: number;
    embeddedSettings: WebChatBannerSettings;
    onChange: (embeddedSettings: WebChatBannerSettings, isValid: boolean) => void;
    onToggle: (key: number) => void;
}

const BannerSettingAccordion: React.FC<BannerSettingAccordionProps> = (props) => {
    const { accordionKey, embeddedSettings, onChange, onToggle } = props;

    const intl = useIntl();

    const onChangeInput = (changes: WebChatBannerSettings) => {
        onChange(
            { ...embeddedSettings, ...changes },
            isValidBannerSettings(changes).isValid
        );
    };

    return (
        <div className="webchat-setting-container">
            <WebchatDisclosure isOpen={accordionKey === 0}
                               onToggle={() => onToggle(0)}
                               title={intl.formatMessage({ id: "i09066" })}
            >
                <BannerButtonInputGroup embeddedSettings={embeddedSettings}
                                        legend={intl.formatMessage({ id: "i09066" })}
                                        onChange={onChangeInput}
                />
            </WebchatDisclosure>

            <WebchatDisclosure isOpen={accordionKey === 1}
                               onToggle={() => onToggle(1)}
                               title={intl.formatMessage({ id: "i09067" })}
            >
                <InstallationInputGroup embeddedSettings={embeddedSettings}
                                        legend={intl.formatMessage({ id: "i09067" })}
                                        onChange={onChangeInput}
                />
            </WebchatDisclosure>
        </div>
    );
};

export default WebchatIntegrationView;
